import React from 'react'
import {Rental} from '../../../common/types/rental'
import {roundToDigits} from '../../../common/utils/roundToDigits'
import {Typography} from '../../../webui/Typography'
import {SortablePaginatedTable} from '../../../webui/table/sortable-paginated-table/SortablePaginatedTable'
import {TableHeadCell} from '../../../webui/table/sortable-paginated-table/TableHeadCell'
import {InfoTooltip} from '../../../webui/table/InfoTooltip'
import {getProductsInRentalsStats, ProductsInRentalsStats} from './utils/getProductsInRentalsStats'

interface Props {
  rentals: Rental[]
}

const createTableHeadCells = (): TableHeadCell<ProductsInRentalsStats>[] => [
  {
    id: 'productName',
    label: (
      <>
        Product
        <InfoTooltip title="The rented product." />
      </>
    ),
    render: (row) => row.productName,
  },
  {
    id: 'rentalsCount',
    label: (
      <>
        # Rentals
        <InfoTooltip title="Total number of rentals for this product during the selected period." />
      </>
    ),
    render: (row) => row.rentalsCount,
  },
  {
    id: 'boxesCount',
    label: (
      <>
        Boxes
        <InfoTooltip title="Number of boxes that had at least one rental of this product during the selected period." />
      </>
    ),
    render: (row) => row.boxesCount,
  },
  {
    id: 'rentalsPerBox',
    label: (
      <>
        R/box
        <InfoTooltip
          title={
            <>
              Average number of rentals per box that actually rented the product.
              <br />
              Formula: rentals ÷ boxes.
            </>
          }
        />
      </>
    ),
    render: (row) => roundToDigits(row.rentalsPerBox, 1),
  },
  {
    id: 'rentalShare',
    label: (
      <>
        Share
        <InfoTooltip title="Percentage of all rentals in the selected period that this product represents." />
      </>
    ),
    render: (row) => `${row.rentalShare}%`,
  },
  {
    id: 'meanValueRatings',
    label: (
      <>
        Rating
        <InfoTooltip title="Average user rating for rentals of this product (only rentals with ratings included)." />
      </>
    ),
    render: (row) => (row.meanValueRatings ? Math.round(100 * row.meanValueRatings) / 100 : '—'),
  },
  {
    id: 'meanValueRentTime',
    label: (
      <>
        Time
        <InfoTooltip title="Average rental duration (in minutes) for this product." />
      </>
    ),
    render: (row) => (row.meanValueRentTime ? Math.round(row.meanValueRentTime) : '—'),
  },
]

export const RentalsPerProductTable: React.FC<Props> = ({rentals}) => {
  const rows = getProductsInRentalsStats(rentals)

  if (rows.length <= 0) {
    return <Typography>{'There were no rentals during this period'}</Typography>
  }

  return (
    <SortablePaginatedTable
      title={'Number of rentals per product'}
      initialRowsPerPage={rows.length}
      initialOrderByKey={'rentalsCount'}
      initialOrder={'desc'}
      tableHeadCells={createTableHeadCells()}
      rows={rows}
    />
  )
}
